import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
// helpers
import { formatDistance, formatDuration } from "./helpers/lastMileMetrics";

export interface LastMileInfoProps {
  title: string;
  icon?: ReactNode;
  destinationName: string;
  stopName?: string | null;
  distanceMeters?: number | null;
  durationSeconds?: number | null;
  googleMapsUrl?: string | null;
  isLoading?: boolean;
  error?: string | null;
  mapsLinkBelow?: boolean;
}

export default function LastMileInfo({
  title,
  icon,
  destinationName,
  stopName = null,
  distanceMeters,
  durationSeconds,
  googleMapsUrl,
  isLoading = false,
  error = null,
  mapsLinkBelow = false
}: LastMileInfoProps) {
  const { t } = useTranslation();
  const distanceLabel = useMemo(() => formatDistance(distanceMeters), [distanceMeters]);
  const durationLabel = useMemo(() => formatDuration(durationSeconds), [durationSeconds]);
  const hasMetrics = Boolean(durationLabel || distanceLabel);
  const showLink = Boolean(googleMapsUrl) && !isLoading && !error;
  const errorMessage = useMemo(() => {
    if (!error) {
      return null;
    }
    const translated = t(error);
    return translated === error ? t("transport.lastMile.error") : translated;
  }, [error, t]);

  const mapsLink = showLink ? (
    <Link
      href={googleMapsUrl as string}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{
        fontFamily: "Neutraface2Book",
        fontSize: 13,
        whiteSpace: "nowrap",
        textDecoration: "underline",
        mr: 2
      }}
      data-cy="open-in-google-maps-route"
    >
      {t("maps.openGoogleMaps")} &gt;
    </Link>
  ) : null;

  return (
    <Box
      sx={{
        px: 0.5,
        py: 1.5,
        mb: 2
      }}
      data-cy="last-mile-info"
    >
      {stopName && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontFamily: "Neutraface2Book", fontSize: 14, mt: 0.75, textTransform: "uppercase" }}>
            {t("transport.lastMile.nearestStop")}
          </Typography>
          <Typography sx={{ fontFamily: "Neutraface2Demi", fontSize: 17, mt: 0.75 }}>
            {t("transport.lastMile.getOff")} {stopName}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontFamily: "Neutraface2Italic", fontSize: 14 }}>{title}</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "flex-start"
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {icon ? (
              <Box sx={{ display: "flex", alignItems: "center" }} mr={1}>
                {icon}
              </Box>
            ) : null}

            <Typography sx={{ fontFamily: "Neutraface2Demi", fontSize: 16 }} noWrap title={destinationName}>
              {destinationName}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontFamily: "Neutraface2Book", fontSize: 13, color: "text.secondary" }}>
                {t("transport.lastMile.loading")}
              </Typography>
            </Box>
          ) : error ? (
            <Typography sx={{ fontFamily: "Neutraface2Book", fontSize: 13, color: "error.main", mt: 0.75 }}>
              {errorMessage}
            </Typography>
          ) : hasMetrics ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 0.75,
                justifyContent: mapsLinkBelow ? "flex-start" : "space-between"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 3.3 }}>
                {durationLabel ? (
                  <Typography sx={{ fontFamily: "Neutraface2Demi", fontSize: 13 }}>{durationLabel}</Typography>
                ) : null}{" "}
                |
                {distanceLabel ? (
                  <Typography sx={{ fontFamily: "Neutraface2Demi", fontSize: 13 }}>{distanceLabel}</Typography>
                ) : null}
              </Box>

              {!mapsLinkBelow ? mapsLink : null}
            </Box>
          ) : null}

          {mapsLinkBelow && mapsLink ? (
            <Box sx={{ mt: 0.75, ml: 3.3 }} data-cy="open-in-google-maps-below">
              {mapsLink}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
